<template>
  <AnalyticsTable :data="data" />
</template>

<script setup lang="ts">
import { GET_ANALYTICS_RENTAL } from '@/utils/queries';

const AnalyticsTable = defineAsyncComponent(
  () => import('@/async-components/Analytics/Table.vue')
);

interface ResponseCurrentRentalData {
  latest_updated_properties: {
    count: number;
    average_list_price_sqft: number | null;
  };
}

interface ResponseRentalData {
  latest_updated_properties: {
    count: number;
  };
}

interface ResponseData {
  current: ResponseCurrentRentalData[];
  period_3_months: ResponseRentalData[];
  period_6_months: ResponseRentalData[];
  period_12_months: ResponseRentalData[];
  period_24_months: ResponseRentalData[];
}

interface RentalData {
  averageListPricePerSQFT: string;
  availableUnitsForRent: number;
  totalUnitsAvailableForRentP: string;
  monthsOfInventory: number;
  unitsRentedPast3Months: number;
  unitsRentedPast6Months: number;
  unitsRentedPast12Months: number;
  unitsRentedPast24Months: number;
}

const props = defineProps({
  buildingName: {
    type: String,
    required: true
  },
  totalUnits: {
    type: Number,
    default: 0
  }
});

const client = useAnalyticsClient();

const { n } = useI18n();

const { language } = useLanguage();

const data = ref<RentalData>(null);

function formatCurrency(value: number | null) {
  return value ? n(value, 'currency', language.value) : null;
}

onMounted(() => {
  client
    .query<ResponseData>({
      query: GET_ANALYTICS_RENTAL,
      variables: {
        buildingName: props.buildingName
      }
    })
    .then(res => {
      const availableUnits =
        res.data.current[0]?.latest_updated_properties.count;

      data.value = {
        averageListPricePerSQFT: formatCurrency(
          res.data.current[0]?.latest_updated_properties.average_list_price_sqft
        ),
        availableUnitsForRent: availableUnits,
        totalUnitsAvailableForRentP: availableUnits
          ? `${((availableUnits / props.totalUnits) * 100).toFixed(2)}%`
          : null,
        monthsOfInventory: null,
        unitsRentedPast3Months:
          res.data.period_3_months[0]?.latest_updated_properties.count,
        unitsRentedPast6Months:
          res.data.period_6_months[0]?.latest_updated_properties.count,
        unitsRentedPast12Months:
          res.data.period_12_months[0]?.latest_updated_properties.count,
        unitsRentedPast24Months:
          res.data.period_24_months[0]?.latest_updated_properties.count
      };
    });
});
</script>

<style scoped lang="scss">
.sub-table {
  & > * {
    @apply px-4 py-2 border-b border-line-light mb-2;
    &:nth-child(even) {
      @apply text-right;
    }
  }
}
</style>
